import React from 'react';
import { View, Text, StyleSheet } from 'react-native';

function clampProgress(p) {
  return Math.max(0, Math.min(100, Math.floor(Number(p) || 0)));
}

function parseHexColor(hex) {
  const h = String(hex || '').trim();
  if (!h.startsWith('#')) return null;
  const raw = h.slice(1);
  if (raw.length === 3) {
    const r = parseInt(raw[0] + raw[0], 16);
    const g = parseInt(raw[1] + raw[1], 16);
    const b = parseInt(raw[2] + raw[2], 16);
    return { r, g, b };
  }
  if (raw.length === 6) {
    const r = parseInt(raw.slice(0, 2), 16);
    const g = parseInt(raw.slice(2, 4), 16);
    const b = parseInt(raw.slice(4, 6), 16);
    return { r, g, b };
  }
  return null;
}

function parseRgbColor(input) {
  const s = String(input || '').trim();
  const m =
    /^rgba?\(\s*([0-9]+)\s*,\s*([0-9]+)\s*,\s*([0-9]+)\s*(?:,\s*([0-9]*\.?[0-9]+)\s*)?\)$/i.exec(
      s,
    );
  if (!m) return null;
  const r = Math.max(0, Math.min(255, parseInt(m[1], 10)));
  const g = Math.max(0, Math.min(255, parseInt(m[2], 10)));
  const b = Math.max(0, Math.min(255, parseInt(m[3], 10)));
  const a = m[4] === undefined ? 1 : Math.max(0, Math.min(1, parseFloat(m[4])));
  return { r, g, b, a };
}

function parseColor(input) {
  return parseHexColor(input) || parseRgbColor(input);
}

function relativeLuminance({ r, g, b }) {
  const toLinear = v => {
    const x = v / 255;
    return x <= 0.04045 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
  };
  const R = toLinear(r);
  const G = toLinear(g);
  const B = toLinear(b);
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}

/**
 * Minimal determinate progress bar (RN + RN Web).
 * Uses a real percentage value (0-100) and displays it centered.
 */
export default function ProgressBarMinimal({
  progress = 0,
  label = '',
  showPercent = true,
  height = 16,
  textColor,
  labelColor,
  style,
}) {
  const safeProgress = clampProgress(progress);

  const fillColor = '#c5e837';
  const trackColor = 'rgba(197,232,55,0.18)';

  const borderRadius = Math.max(2, Math.floor(height / 4));
  const containerStyle = [styles.container, style];

  const bgForText = safeProgress >= 50 ? fillColor : trackColor;
  const parsed = parseColor(bgForText);
  const bgRgb = parsed
    ? {
      r: Math.round(parsed.r * (parsed.a ?? 1)),
      g: Math.round(parsed.g * (parsed.a ?? 1)),
      b: Math.round(parsed.b * (parsed.a ?? 1)),
    }
    : null;
  const lum = bgRgb ? relativeLuminance(bgRgb) : null;
  const autoTextColor = lum !== null && lum > 0.6 ? '#000000' : '#ffffff';
  const resolvedTextColor = textColor || autoTextColor;
  const resolvedLabelColor = labelColor || 'rgba(255,255,255,0.72)';

  return (
    <View style={containerStyle}>
      {label ? (
        <Text style={[styles.label, { color: resolvedLabelColor }]}>{label}</Text>
      ) : null}

      <View
        style={[
          styles.track,
          {
            height,
            borderRadius,
            backgroundColor: trackColor,
          },
        ]}>
        <View
          style={[
            styles.fill,
            {
              height,
              borderRadius,
              backgroundColor: fillColor,
              width: `${safeProgress}%`,
            },
          ]}
        />

        {showPercent ? (
          <View style={styles.centerTextWrap} pointerEvents="none">
            <Text style={[styles.centerText, { color: resolvedTextColor }]}>
              {safeProgress}%
            </Text>
          </View>
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: 260,
  },
  label: {
    fontFamily: 'Verdana',
    fontSize: 14,
    fontWeight: '400',
    textAlign: 'center',
    marginBottom: 12,
  },
  track: {
    position: 'relative',
    width: '100%',
    overflow: 'hidden',
  },
  fill: {
    width: '0%',
  },
  centerTextWrap: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  centerText: {
    fontFamily: 'Verdana',
    fontSize: 14,
    fontWeight: '400',
  },
});
